import { set as setDB, get as getDB } from "idb-keyval";
import { ASSETS_URI, ECOLLAT_CACHE, HOST, PDF_DB } from "../config";
import { getAllPdf } from "./index";
import Service from "./service";
import { PwaAnalatics } from "../Functions/analytic";

// localStorage key holding a { [pdfID]: pdfVersion } map used to decide
// whether the images cached for a PDF are still valid.
const PDF_IMG_VERSIONS = "pdf_img_versions";

const User = window.__E_TT_Client;
const client = User.companySlug;
const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

let progressBar;
let timeout;
let processCount = 0;

// The catalogue images live on a different origin that serves no CORS headers,
// so every request has to be made no-cors. The responses come back opaque,
// which Cache Storage accepts and <img> renders normally. Requesting them in
// the default (cors) mode still puts the bytes on the wire and then throws the
// response away at the CORS check, which is pure waste.
const imageRequest = (url) => new Request(url, { mode: "no-cors" });

// A PDF is hundreds of pages. Firing them through Promise.all starts every
// request in the same tick; over HTTP/2 there is no connection limit to save
// us, so the prefetch competes with the pages the reader is actually looking
// at. Drain the list through a small pool instead.
const IMAGE_FETCH_CONCURRENCY = 4;

const runWithConcurrency = async (items, limit, worker) => {
  const queue = items.slice();
  const workers = Array.from(
    { length: Math.min(limit, queue.length) },
    async () => {
      while (queue.length > 0) {
        await worker(queue.shift());
      }
    }
  );
  await Promise.all(workers);
};

// Bulk image work is for installed PWAs only - on the website the service
// worker caches pages as the reader opens them.
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

const updateProgressBar = (barCount, removeBar = false, showError = false) => {
  if (!progressBar) {
    const root = document.getElementsByTagName("body")[0];

    const loadingBar = document.createElement("div");
    loadingBar.classList.add("pwa-progressbar");

    const fillBar = document.createElement("span");
    fillBar.classList.add("pwa-install-progress");
    loadingBar.appendChild(fillBar);

    progressBar = {
      bar: loadingBar,
      progress: fillBar,
    };

    root.appendChild(loadingBar);
  }

  if (progressBar.progress) {
    progressBar.progress.style.width = `${barCount}%`;
  }

  if (showError && progressBar.progress) {
    progressBar.progress.classList.add("pwa-error");
  }

  if (removeBar) {
    timeout = setTimeout(() => {
      clearTimeout(timeout);
      if (progressBar.bar) {
        progressBar.bar.remove();
        processCount = 0;
      }
    }, 2000);
  }
};
// Function to make a POST request
async function makePostRequest(url, data) {
  try {
    const response = Service.post(url, data, {
      headers: {
        "Content-Type": "application/json",
        // Add any other headers if needed
      },
    });

    // Axios automatically throws an error for non-2xx responses
    const jsonResponse = response.data;
    return jsonResponse;
  } catch (error) {
    console.error("Error making POST request:", error);
    throw error;
  }
}
export const getAllImagesUri = (pdfs) => {
  const size = window.innerWidth > 768 ? 0 : 1;

  if (pdfs && pdfs.length > 0) {
    let imgToCache = [];

    imgToCache.push(`${HOST}/allpagesforimages/pages/${window.__E_TT_Client.companySlug}`);
    pdfs.map(async (pdf) => {
      for (let i = 1; i <= pdf.totalPages; i++) {
        imgToCache.push(
          `${ASSETS_URI}/${window.__E_TT_Client.companySlug}/${pdf.pdfID}/${i}/image-${size}.jpg`
        );
        // imgToCache.push(`${HOST}/view-image.dll?id=${pdf.pdfID}&count=${i}&imagesize=${size}`);
      }
      // imgToCache.push(response);
      // imgToCache.push(`${HOST}/searchaccordingtoslug/${pdf.pdfSlug}`);
      // imgToCache.push(`${HOST}/${client}/${pdf.pdfID}/Links/menu.json`);
      // imgToCache.push(`${HOST}/getlinks/${pdf.pdfID}`);
      if (pdf.pdfcoverPath) imgToCache.push(pdf.pdfcoverPath);
      return pdf;
    });
    return imgToCache;
  }
  return [];
};

// Build the exact image URIs the viewer will request for a single PDF, so the
const getPdfImageUris = (pdf) => {
  if (!pdf || !pdf.pdfID || !pdf.totalPages) return [];

  const size = window.innerWidth > 768 ? 0 : 1;
  const companyName =
    (pdf.coverImage && pdf.coverImage.split("/")[0]) ||
    window.__E_TT_Client.companySlug;

  const uris = [];
  for (let i = 1; i <= pdf.totalPages; i++) {
    let page = i;
    if (pdf.parent && pdf.pdfSlug && pdf.pages && pdf.pages[i - 1]) {
      page = parseInt(pdf.pages[i - 1]);
    }
    uris.push(
      `${ASSETS_URI}/${companyName}/${pdf.pdfID}/${page}/image-${size}.jpg`
    );
  }
  if (pdf.coverImage) {
    uris.push(`${ASSETS_URI}/${pdf.coverImage}`);
  }
  return uris;
};

const readPdfImgVersions = () => {
  try {
    return JSON.parse(localStorage.getItem(PDF_IMG_VERSIONS) || "{}") || {};
  } catch (e) {
    return {};
  }
};

// Fetch every image of a PDF and store it in the Cache Storage bucket used by
// the service worker. Runs in the background, best-effort.
const putPdfImagesInCache = async (uris) => {
  try {
    const cache = await caches.open(ECOLLAT_CACHE);
    await runWithConcurrency(uris, IMAGE_FETCH_CONCURRENCY, async (url) => {
      try {
        const req = imageRequest(url);
        // An interrupted sync leaves the cache half full; skipping what is
        // already there makes the next run resume instead of restart.
        if (await cache.match(req)) return;

        const res = await fetch(req);
        if (res && (res.ok || res.type === "opaque")) {
          await cache.put(req, res);
        }
      } catch (e) {
        // ignore a single image failure
      }
    });
  } catch (e) {
    // caching is best-effort
  }
};

/**
 * Version-aware image cache for a single PDF (used by the slug viewer).
 *
 * - Stores the PDF's version in localStorage keyed by pdfID.
 * - Version unchanged: nothing to do. The service worker serves whatever it
 *   already has and caches the rest as the reader turns pages.
 * - Version changed (or this device has never seen the PDF): the stale pages
 *   are dropped so the cache-first worker refetches them. Only an installed
 *   PWA pre-downloads the whole PDF; on the website that would mean hundreds
 *   of requests for pages nobody may ever open.
 *
 * The invalidation is awaited so the very next <img> request misses the cache;
 * the re-download runs in the background.
 */
export const cachePdfImagesByVersion = async (pdf) => {
  try {
    if (!pdf || !pdf.pdfID || typeof caches === "undefined") return;

    const uris = getPdfImageUris(pdf);
    if (uris.length === 0) return;

    const versions = readPdfImgVersions();
    const pdfKey = String(pdf.pdfID);
    const currentVersion = pdf.pdfVersion;
    const storedVersion = versions[pdfKey];

    const versionMatches =
      storedVersion !== undefined &&
      String(storedVersion) === String(currentVersion);

    if (versionMatches) return;

    // Version changed (or first visit) — drop this PDF's stale images.
    const cache = await caches.open(ECOLLAT_CACHE);
    await runWithConcurrency(uris, IMAGE_FETCH_CONCURRENCY, (url) =>
      cache.delete(imageRequest(url))
    );

    versions[pdfKey] = currentVersion;
    try {
      localStorage.setItem(PDF_IMG_VERSIONS, JSON.stringify(versions));
    } catch (e) {}

    if (isStandalone()) putPdfImagesInCache(uris);
  } catch (e) {
    // caching must never break the viewer
  }
};

/**
 * Reconcile the offline image cache with the catalogue the API just returned.
 *
 * A PDF is only touched when it is new to this device or its pdfVersion moved
 * - everything else is already cached and correct. Stale pages are dropped on
 * every client (otherwise the cache-first worker would serve the old version
 * forever), but the re-download only runs in an installed PWA.
 *
 * This replaces updatePdfsOffline + PedfVersionRemoveCache, which ran in that
 * order: the first wrote the fresh catalogue to PDF_DB, so the second compared
 * the new versions against themselves and never found a change to evict.
 */
export const syncPdfsOffline = async (pdfs) => {
  try {
    if (!pdfs || pdfs.length === 0 || typeof caches === "undefined") return;

    const stored = await getDB(PDF_DB)
      .then((res) => res && JSON.parse(res))
      .catch(() => null);

    const storedById = new Map(
      (Array.isArray(stored) ? stored : []).map((p) => [String(p.pdfID), p])
    );

    const changed = pdfs.filter((pdf) => {
      const previous = storedById.get(String(pdf.pdfID));
      if (!previous) return true; // never seen on this device
      return String(previous.pdfVersion) !== String(pdf.pdfVersion);
    });

    if (changed.length === 0) {
      setDB(PDF_DB, JSON.stringify(pdfs));
      return;
    }

    const imageUris = getAllImagesUri(changed);
    await RemovecacheImages(imageUris);

    if (!isStandalone()) {
      setDB(PDF_DB, JSON.stringify(pdfs));
      return;
    }

    // Don't block the caller on a few hundred images - but only record the
    // catalogue once they are actually stored, so an interrupted sync is
    // retried on the next load instead of being marked done.
    putPdfImagesInCache(imageUris).then(() =>
      setDB(PDF_DB, JSON.stringify(pdfs))
    );
  } catch (e) {
    // offline sync is best-effort
  }
};
export const RemovecacheImages = async (imageUris = false, savePdfData = false) => {
  let pdfs;
  if (!imageUris) {
    pdfs = await getAllPdf(client, false);
    imageUris = getAllImagesUri(pdfs, client);
  }

  if (imageUris.length === 0) return true;

  try {
    const cache = await caches.open(ECOLLAT_CACHE).catch((e) => false);

    if (cache && imageUris) {
      await runWithConcurrency(imageUris, IMAGE_FETCH_CONCURRENCY, (url) =>
        cache.delete(imageRequest(url))
      );
    }

    if (pdfs && savePdfData) {
      setDB(PDF_DB, JSON.stringify(pdfs));
    }

    return true;
  } catch (e) {
    // alert("An error has occurred, please reinstall the app.");
    return e;
  }
};


export const cacheImages = async (imageUris = false, savePdfData = false) => {
  let pdfs;
  if (!imageUris) {
    pdfs = await getAllPdf(client, false);
    imageUris = getAllImagesUri(pdfs, client);
  }

  if (imageUris.length === 0) return true;

  try {
    const cache = await caches.open(ECOLLAT_CACHE).catch((e) => false);

    if (cache && imageUris) {
      let failures = 0;

      await runWithConcurrency(
        imageUris,
        IMAGE_FETCH_CONCURRENCY,
        async (url) => {
          const req = imageRequest(url);
          const res = await fetch(req).catch(() => false);

          if (res && (res.ok || res.type === "opaque")) {
            await cache.put(req, res);
          } else {
            failures++;
          }

          processCount++;
          updateProgressBar(
            Math.round((processCount / imageUris.length) * 100)
          );
        }
      );

      if (failures > 7) {
        throw new Error("Few errors found while syncing");
      }
    }

    if (pdfs && savePdfData) {
      setDB(PDF_DB, JSON.stringify(pdfs));
    }

    updateProgressBar(100, true);
    if (iOS) {
      alert("Successfully synced.");
    } else {
      alert("Successfully synced.");
    }
    return true;
  } catch (e) {
    updateProgressBar(processCount, true, true);
    alert("An error has occurred, please reinstall the app.");
    return e;
  }
};

window.addEventListener("appinstalled", (event) => {



  PwaAnalatics({
    companySlug: window.__E_TT_Client.companySlug,
    isMobile: window.innerWidth > 768 ? false : true
  })






  cacheImages(false, true).then((res) => {
  });
});
